
import * as THREE from "../../libs/three.js/build/three.module.js";
import {ElevationZone} from "./ElevationZone.js";
import {EventDispatcher} from "../EventDispatcher.js";

export class ElevationZoneTool extends EventDispatcher {

	constructor(viewer) {
		super();

		this.viewer = viewer;
		this.maxPolygonVertices = 8;

		this.addEventListener("start_inserting_elevation_zone", e => {
			this.viewer.dispatchEvent({
				type: "cancel_insertions"
			});
		});
	}

	setScene(scene) {
		if (this.scene === scene) {
			return;
		}
		this.scene = scene;
	}

	startInsertion() {
		let domElement = this.viewer.renderer.domElement;
		let canvasSize = this.viewer.renderer.getSize(new THREE.Vector2());

		// Create SVG overlay for polygon drawing
		let svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
		svg.setAttribute("width", canvasSize.width);
		svg.setAttribute("height", canvasSize.height);
		svg.style.position = "absolute";
		svg.style.pointerEvents = "none";
		svg.style.top = "0";
		svg.style.left = "0";
		domElement.parentElement.appendChild(svg);

		// Completed edges polyline (dashed black + white)
		let polyBg = document.createElementNS("http://www.w3.org/2000/svg", "polyline");
		polyBg.setAttribute("fill", "none");
		polyBg.setAttribute("stroke", "rgb(0,0,0)");
		polyBg.setAttribute("stroke-width", "4");
		polyBg.setAttribute("stroke-dasharray", "9, 6");
		svg.appendChild(polyBg);

		let polyFg = document.createElementNS("http://www.w3.org/2000/svg", "polyline");
		polyFg.setAttribute("fill", "none");
		polyFg.setAttribute("stroke", "rgb(255,255,255)");
		polyFg.setAttribute("stroke-width", "2");
		polyFg.setAttribute("stroke-dasharray", "5, 10");
		svg.appendChild(polyFg);

		// Live preview line from last vertex to cursor
		let previewLine = document.createElementNS("http://www.w3.org/2000/svg", "line");
		previewLine.setAttribute("stroke", "rgba(255,255,0,0.8)");
		previewLine.setAttribute("stroke-width", "2");
		previewLine.setAttribute("stroke-dasharray", "4, 4");
		previewLine.style.display = "none";
		svg.appendChild(previewLine);

		// Closing line from cursor back to first vertex
		let closingLine = document.createElementNS("http://www.w3.org/2000/svg", "line");
		closingLine.setAttribute("stroke", "rgba(0,255,255,0.6)");
		closingLine.setAttribute("stroke-width", "2");
		closingLine.setAttribute("stroke-dasharray", "4, 4");
		closingLine.style.display = "none";
		svg.appendChild(closingLine);

		// First vertex close indicator (filled circle)
		let closeIndicator = document.createElementNS("http://www.w3.org/2000/svg", "circle");
		closeIndicator.setAttribute("r", "8");
		closeIndicator.setAttribute("fill", "rgba(0,255,255,0.5)");
		closeIndicator.setAttribute("stroke", "white");
		closeIndicator.setAttribute("stroke-width", "2");
		closeIndicator.style.display = "none";
		svg.appendChild(closeIndicator);

		// Vertex markers group
		let vertexMarkers = document.createElementNS("http://www.w3.org/2000/svg", "g");
		svg.appendChild(vertexMarkers);

		let zone = new ElevationZone(this.viewer.scene.getActiveCamera().clone());

		this.dispatchEvent({"type": "start_inserting_elevation_zone"});

		this.viewer.scene.addElevationZone(zone);

		let screenPoints = []; // {x, y} in DOM pixels

		let updatePolylines = () => {
			let pointStr = screenPoints.map(p => `${p.x},${p.y}`).join(" ");
			polyBg.setAttribute("points", pointStr);
			polyFg.setAttribute("points", pointStr);
		};

		let addVertexMarker = (x, y) => {
			let circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
			circle.setAttribute("cx", x);
			circle.setAttribute("cy", y);
			circle.setAttribute("r", "5");
			circle.setAttribute("fill", "white");
			circle.setAttribute("stroke", "black");
			circle.setAttribute("stroke-width", "2");
			vertexMarkers.appendChild(circle);
		};

		let isNearFirstVertex = (x, y) => {
			if (screenPoints.length < 3) {
				return false;
			}
			let first = screenPoints[0];
			let dx = x - first.x;
			let dy = y - first.y;
			return Math.sqrt(dx * dx + dy * dy) < 20;
		};

		let onMouseMove = (e) => {
			let rect = domElement.getBoundingClientRect();
			let x = e.clientX - rect.left;
			let y = e.clientY - rect.top;

			if (screenPoints.length > 0) {
				let last = screenPoints[screenPoints.length - 1];
				previewLine.setAttribute("x1", last.x);
				previewLine.setAttribute("y1", last.y);
				previewLine.setAttribute("x2", x);
				previewLine.setAttribute("y2", y);
				previewLine.style.display = "";

				// Show closing line when we have >= 2 vertices
				if (screenPoints.length >= 2) {
					let first = screenPoints[0];
					closingLine.setAttribute("x1", x);
					closingLine.setAttribute("y1", y);
					closingLine.setAttribute("x2", first.x);
					closingLine.setAttribute("y2", first.y);
					closingLine.style.display = "";
				}

				// Highlight close indicator when near first vertex
				if (isNearFirstVertex(x, y)) {
					closeIndicator.setAttribute("fill", "rgba(0,255,255,0.9)");
					closeIndicator.setAttribute("r", "12");
				} else if (screenPoints.length >= 3) {
					closeIndicator.setAttribute("fill", "rgba(0,255,255,0.5)");
					closeIndicator.setAttribute("r", "8");
				}
			}
		};

		let finalize = () => {
			// Remove event listeners
			domElement.removeEventListener("mouseup", onMouseUp, true);
			domElement.removeEventListener("mousemove", onMouseMove, true);
			domElement.removeEventListener("contextmenu", onContextMenu, true);
			this.viewer.removeEventListener("cancel_insertions", cancel);

			// Remove SVG
			svg.remove();

			// Re-enable viewer input
			this.viewer.inputHandler.enabled = true;
		};

		let cancel = () => {
			this.viewer.scene.removeElevationZone(zone);
			finalize();
		};

		let complete = () => {
			zone.initialized = true;
			finalize();

			// Compute Z range from points inside the polygon
			this.computeZRange(zone);
		};

		let onMouseUp = (e) => {
			if (e.button === 0) { // Left click
				let rect = domElement.getBoundingClientRect();
				let x = e.clientX - rect.left;
				let y = e.clientY - rect.top;

				// Check if closing the polygon
				if (isNearFirstVertex(x, y)) {
					complete();
					return;
				}

				// Add new vertex
				let size = this.viewer.renderer.getSize(new THREE.Vector2());
				let ndcX = 2.0 * (x / size.width) - 1.0;
				let ndcY = -2.0 * (y / size.height) + 1.0;

				let marker = zone.addMarker();
				marker.position.set(ndcX, ndcY, 0);

				screenPoints.push({x, y});
				updatePolylines();
				addVertexMarker(x, y);

				// Show close indicator after first vertex
				if (screenPoints.length === 1) {
					closeIndicator.setAttribute("cx", x);
					closeIndicator.setAttribute("cy", y);
				}
				if (screenPoints.length >= 3) {
					closeIndicator.style.display = "";
				}

				// Auto-close if max vertices reached
				if (zone.markers.length >= this.maxPolygonVertices) {
					complete();
				}
			} else if (e.button === 2) { // Right click
				cancel();
			}
		};

		let onContextMenu = (e) => {
			e.preventDefault();
		};

		this.viewer.addEventListener("cancel_insertions", cancel);
		domElement.addEventListener("mouseup", onMouseUp, true);
		domElement.addEventListener("mousemove", onMouseMove, true);
		domElement.addEventListener("contextmenu", onContextMenu, true);

		// Disable viewer input during polygon drawing
		this.viewer.inputHandler.enabled = false;

		return zone;
	}

	computeZRange(zone) {
		let viewMatrix = zone.viewMatrix;
		let projMatrix = zone.projMatrix;
		let vpMatrix = new THREE.Matrix4().copy(projMatrix).multiply(viewMatrix);

		let zValues = [];

		let pointclouds = this.viewer.scene.pointclouds;

		for (let pointcloud of pointclouds) {
			if (!pointcloud.visible) {
				continue;
			}

			let nodes = pointcloud.visibleNodes;
			if (!nodes) {
				continue;
			}

			for (let node of nodes) {
				let geometry = node.geometryNode.geometry;
				if (!geometry) {
					continue;
				}

				let positionAttribute = geometry.attributes.position;
				if (!positionAttribute) {
					continue;
				}

				// Each node has its own world matrix via sceneNode
				let nodeWorld = node.sceneNode.matrixWorld;
				let wvp = new THREE.Matrix4().copy(vpMatrix).multiply(nodeWorld);

				let positions = positionAttribute.array;
				let numPoints = positionAttribute.count;
				let classificationAttr = geometry.attributes.classification;
				let classificationArray = classificationAttr ? classificationAttr.array : null;

				// Sample every Nth point for performance
				let step = Math.max(1, Math.floor(numPoints / 1000));

				for (let i = 0; i < numPoints; i += step) {
					// Skip points whose classification is filtered out
					if (classificationArray) {
						let classId = classificationArray[i];
						let classEntry = this.viewer.classifications[classId];
						if (classEntry && classEntry.visible === false) {
							continue;
						}
					}

					let x = positions[i * 3 + 0];
					let y = positions[i * 3 + 1];
					let z = positions[i * 3 + 2];

					// Project to NDC using zone's WVP matrix
					let px = wvp.elements[0] * x + wvp.elements[4] * y + wvp.elements[8] * z + wvp.elements[12];
					let py = wvp.elements[1] * x + wvp.elements[5] * y + wvp.elements[9] * z + wvp.elements[13];
					let pw = wvp.elements[3] * x + wvp.elements[7] * y + wvp.elements[11] * z + wvp.elements[15];

					let ndcX = px / pw;
					let ndcY = py / pw;

					// 2D point-in-polygon test
					if (this.pointInPolygon(ndcX, ndcY, zone.markers)) {
						// Get world Z via the node's world matrix
						let worldZ = nodeWorld.elements[2] * x + nodeWorld.elements[6] * y + nodeWorld.elements[10] * z + nodeWorld.elements[14];
						zValues.push(worldZ);
					}
				}
			}
		}

		let filteredRange = this.computeFilteredRange(zValues);
		if (filteredRange !== null) {
			zone.zRange.min = filteredRange.min;
			zone.zRange.max = filteredRange.max;
			zone.zFences.low = filteredRange.fenceLow;
			zone.zFences.high = filteredRange.fenceHigh;
			zone.zRangeComputed = true;
		} else {
			zone.zRange.min = 0;
			zone.zRange.max = 1;
			zone.zRangeComputed = true;
		}
	}

	// R-7 linear interpolation percentile on a pre-sorted array
	percentile(sorted, p) {
		let n = sorted.length;
		let index = (n - 1) * p;
		let lower = Math.floor(index);
		let upper = Math.ceil(index);
		let weight = index - lower;

		if (upper >= n) {
			return sorted[n - 1];
		}
		return sorted[lower] * (1 - weight) + sorted[upper] * weight;
	}

	// Filter Z outliers using IQR fences and return the meaningful range
	computeFilteredRange(zValues) {
		if (zValues.length === 0) {
			return null;
		}

		if (zValues.length === 1) {
			return { min: zValues[0], max: zValues[0] + 1, fenceLow: -1e38, fenceHigh: 1e38 };
		}

		zValues.sort((a, b) => a - b);

		let q1 = this.percentile(zValues, 0.25);
		let q3 = this.percentile(zValues, 0.75);
		let iqr = q3 - q1;

		// If IQR is zero but there is actual range, use the full sorted extent
		if (iqr === 0) {
			let fullMin = zValues[0];
			let fullMax = zValues[zValues.length - 1];
			if (fullMin < fullMax) {
				return { min: fullMin, max: fullMax, fenceLow: -1e38, fenceHigh: 1e38 };
			}
			// All values identical
			return { min: fullMin, max: fullMin + 1, fenceLow: -1e38, fenceHigh: 1e38 };
		}

		let lowerFence = q1 - 1.5 * iqr;
		let upperFence = q3 + 1.5 * iqr;

		// Find actual min/max within fences from the sorted array
		let filteredMin = null;
		let filteredMax = null;

		for (let i = 0; i < zValues.length; i++) {
			if (zValues[i] >= lowerFence) {
				filteredMin = zValues[i];
				break;
			}
		}

		for (let i = zValues.length - 1; i >= 0; i--) {
			if (zValues[i] <= upperFence) {
				filteredMax = zValues[i];
				break;
			}
		}

		// Safety: if filtering collapsed the range, add 1m band
		if (filteredMin === null || filteredMax === null || filteredMin >= filteredMax) {
			let center = filteredMin !== null ? filteredMin : zValues[0];
			return { min: center, max: center + 1, fenceLow: lowerFence, fenceHigh: upperFence };
		}

		return { min: filteredMin, max: filteredMax, fenceLow: lowerFence, fenceHigh: upperFence };
	}

	// Ray-casting point-in-polygon test in 2D
	pointInPolygon(px, py, markers) {
		let inside = false;
		let n = markers.length;

		for (let i = 0, j = n - 1; i < n; j = i++) {
			let xi = markers[i].position.x;
			let yi = markers[i].position.y;
			let xj = markers[j].position.x;
			let yj = markers[j].position.y;

			if (((yi > py) !== (yj > py)) &&
				(px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
				inside = !inside;
			}
		}

		return inside;
	}

	update() {

	}
}
